import React, { useState } from 'react';
import GlobalDialog from '../../../../../components/mui/Dialogue/GlobalDialog';
import { Box, Button, CircularProgress, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { CancelButton } from '../../../../../components/mui/Buttons/Buttons';
import { Icon } from '@iconify/react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { getAllGoals, getPendingEditRequestCount, reviewEditRequest } from '../../../../../redux/features/appraisalSlice';

// One current-vs-proposed row. Only rendered when the proposed value is
// actually present in the request (a request only ever stages the fields
// the requester changed).
const DiffRow = ({ label, current, proposed }) => {
  if (proposed === undefined) return null;
  const changed = String(current ?? '') !== String(proposed ?? '');
  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography
          variant="body2"
          sx={{ textDecoration: changed ? 'line-through' : 'none', color: 'text.secondary' }}
        >
          {current || '—'}
        </Typography>
        {changed && (
          <>
            <Icon icon="mdi:arrow-right" width="14" height="14" />
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
              {proposed || '—'}
            </Typography>
          </>
        )}
      </Stack>
    </Box>
  );
};

// Goal Modification Workflow (REQ-3): review UI for a goal owner's pending
// edit request - shown to the reporting head, who is the only one who can
// decide it. Approving applies the staged changes; rejecting discards them.
const EditRequestReview = ({ goal, formikValues, meta }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [remarks, setRemarks] = useState('');
  // Tracks which button was clicked ('approve' | 'reject' | null) so only
  // that one shows a spinner, and both are disabled meanwhile - the global
  // appraisal.loading flag isn't wired to this action, so relying on it left
  // the buttons showing no feedback at all while the request was in flight.
  const [submittingDecision, setSubmittingDecision] = useState(null);

  const editRequest = goal?.pendingEditRequests?.[0];
  if (!editRequest) return null;

  const changes = editRequest.proposed_changes || {};

  const decide = (decision) => {
    setSubmittingDecision(decision);
    dispatch(
      reviewEditRequest({ edit_request_id: editRequest.edit_request_id, decision, remarks: remarks || undefined })
    ).then((res) => {
      if (res.payload?.status === 'success') {
        enqueueSnackbar(res.payload.message, { variant: 'success' });
        dispatch(getAllGoals(formikValues));
        dispatch(getPendingEditRequestCount());
        setOpen(false);
        setRemarks('');
      } else {
        enqueueSnackbar(res.payload?.message || 'Something went wrong', { variant: 'error' });
      }
      setSubmittingDecision(null);
    });
  };

  return (
    <GlobalDialog
      title="Review Edit Request"
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth="xs"
      closeButton
      popupButton={
        <Tooltip title="Click to review and approve or reject this change" arrow>
          <Box
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              cursor: 'pointer',
              px: 1.5,
              py: 0.75,
              my: 0.5,
              borderRadius: 1.5,
              bgcolor: 'rgba(255, 136, 0, 0.14)',
              border: '1px solid #eed490',
              color: '#b25e00',
              transition: 'all 0.15s ease',
              '&:hover': {
                bgcolor: 'rgba(255, 136, 0, 0.3)',
                borderColor: '#b25e00',
                boxShadow: '0 0 0 1px #b25e00',
              },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Icon icon="mdi:pencil-clock" width="16" height="16" />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                This goal has a pending edit request - click to review
              </Typography>
            </Stack>
            <Icon icon="mdi:chevron-right" width="18" height="18" />
          </Box>
        </Tooltip>
      }
      content={
        <Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            {goal?.goal_name} - proposed changes:
          </Typography>
          <DiffRow label="Name" current={goal?.goal_name} proposed={changes.goal_name} />
          <DiffRow label="Description" current={goal?.goal_description} proposed={changes.goal_description} />
          <DiffRow label="Start Date" current={goal?.goal_start_date} proposed={changes.goal_start_date} />
          <DiffRow label="End Date" current={goal?.goal_end_date} proposed={changes.endDate} />

          <TextField
            label="Remarks (optional)"
            fullWidth
            multiline
            minRows={2}
            size="small"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            sx={{ mt: 2 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
            <Stack direction="row" spacing={1}>
              <CancelButton size="small" disabled={!!submittingDecision} action={() => setOpen(false)}>
                Cancel
              </CancelButton>
              <Button
                disabled={!!submittingDecision}
                size="small"
                variant="outlined"
                color="error"
                startIcon={submittingDecision === 'reject' ? <CircularProgress size={14} color="inherit" /> : null}
                onClick={() => decide('reject')}
              >
                {submittingDecision === 'reject' ? 'Rejecting...' : 'Reject'}
              </Button>
              <Button
                disabled={!!submittingDecision}
                size="small"
                variant="contained"
                color="success"
                startIcon={submittingDecision === 'approve' ? <CircularProgress size={14} color="inherit" /> : null}
                onClick={() => decide('approve')}
              >
                {submittingDecision === 'approve' ? 'Approving...' : 'Approve'}
              </Button>
            </Stack>
          </Box>
        </Box>
      }
    />
  );
};

export default EditRequestReview;
